// src/components/UserCard.jsx
//
// User card used by the Admin/User Management page.
// ==================================================
//
// This component is the compact/mobile representation of one user row. It is
// deliberately kept as a presentational component:
// - it does not fetch users,
// - it does not know backend endpoints,
// - it does not own admin save/delete/reset logic,
// - it only delegates actions back to UserManagementPage through callbacks.
//
// i18n note:
// - Backend role values such as "user", "superuser" and "reception" remain
//   stable technical values.
// - Only labels, placeholders, buttons and accessibility strings are translated.
// - Do not move persistence logic into this component when translating it.

import React from "react";
import { useTranslation } from "react-i18next";

export default function UserCard({
  user,
  passwordValue,
  onPasswordChange,
  onFieldUpdate,
  onResetPassword,
  onOpenSettings,
  onDelete,
  isLoading
}) {
  const { t } = useTranslation(["admin"]);

  return (
    <div className="user-card">
      {/* Username (read-only) */}
      <div className="uc-row">
        <span className="uc-label">{t("admin:userManagement.table.username")}</span>
        <span className="uc-value">{user.username}</span>
      </div>

      {/* First name */}
      <div className="uc-row">
        <span className="uc-label">{t("admin:userManagement.table.firstName")}</span>
        <input
          type="text"
          className="uc-input"
          defaultValue={user.first_name || ""}
          onBlur={(e) => onFieldUpdate(user.id, "first_name", e.target.value)}
          aria-label={t("admin:userManagement.aria.editFirstNameFor", { username: user.username })}
        />
      </div>

      {/* Last name */}
      <div className="uc-row">
        <span className="uc-label">{t("admin:userManagement.table.lastName")}</span>
        <input
          type="text"
          className="uc-input"
          defaultValue={user.last_name || ""}
          onBlur={(e) => onFieldUpdate(user.id, "last_name", e.target.value)}
          aria-label={t("admin:userManagement.aria.editLastNameFor", { username: user.username })}
        />
      </div>

      {/* Role dropdown */}
      <div className="uc-row">
        <span className="uc-label">{t("admin:userManagement.table.role")}</span>
        <select
          className="uc-input"
          defaultValue={user.role || ""}
          onBlur={(e) => onFieldUpdate(user.id, "role", e.target.value)}
          aria-label={t("admin:userManagement.aria.changeRoleFor", { username: user.username })}
        >
          <option value="user">{t("admin:roles.user")}</option>
          <option value="superuser">{t("admin:roles.superuser")}</option>
          <option value="reception">{t("admin:roles.reception")}</option>
        </select>
      </div>

      {/* Approved checkbox */}
      <div className="uc-row">
        <span className="uc-label">{t("admin:userManagement.table.approved")}</span>
        <input
          type="checkbox"
          className="uc-checkbox"
          defaultChecked={!!user.is_approved}
          onChange={(e) => onFieldUpdate(user.id, "is_approved", e.target.checked)}
          aria-label={t("admin:userManagement.aria.approveUser", { username: user.username })}
        />
      </div>

      {/* Active checkbox */}
      <div className="uc-row">
        <span className="uc-label">{t("admin:userManagement.table.active")}</span>
        <input
          type="checkbox"
          className="uc-checkbox"
          defaultChecked={!!user.active}
          onChange={(e) => onFieldUpdate(user.id, "active", e.target.checked)}
          aria-label={t("admin:userManagement.aria.activateOrDeactivateUser", { username: user.username })}
        />
      </div>

      {/* New password field */}
      <div className="uc-row">
        <span className="uc-label">{t("admin:userManagement.table.newPassword")}</span>
        <input
          type="password"
          className="uc-input"
          placeholder={t("admin:userManagement.fields.newPassword")}
          value={passwordValue || ""}
          onChange={(e) => onPasswordChange(user.id, e.target.value)}
          aria-label={t("admin:userManagement.aria.enterNewPasswordFor", { username: user.username })}
        />
      </div>

      {/* Action buttons */}
      <div className="uc-actions">
        <button
          className="reset-button"
          onClick={() => onResetPassword(user.id)}
          disabled={isLoading}
          aria-label={t("admin:userManagement.aria.resetPasswordFor", { username: user.username })}
        >
          {t("admin:userManagement.actions.resetPasswordTitle")}
        </button>
        <button
          className="settings-button"
          onClick={() => onOpenSettings(user.id)}
          aria-label={t("admin:userManagement.aria.openSettingsFor", { username: user.username })}
        >
          {t("admin:userManagement.actions.settings")}
        </button>
        <button
          className="delete-button"
          onClick={() => onDelete(user.id)}
          aria-label={t("admin:userManagement.aria.deleteUser", { username: user.username })}
        >
          {t("admin:userManagement.actions.delete")}
        </button>
      </div>
    </div>
  );
}
